import { defineStore } from 'pinia'
import { useSupabase } from './supabase'
import { ref } from 'vue'

export type Comment = {
  id: string
  photo_id: string
  user_id: string
  content: string
  username: string
  avatar: string
  created_at: string
  updated_at: string
}

export const useComments = defineStore('comments', () => {
  const supabase = useSupabase()
  const comments = ref<Record<string, Comment[]>>({}) // photo_id -> comments[]
  const loading = ref<Record<string, boolean>>({}) // photo_id -> loading state

  async function fetchComments(photoId: string): Promise<Comment[]> {
    if (supabase.user.status !== 'SignedIn') return []

    loading.value[photoId] = true

    try {
      const { data, error } = await supabase.api
        .from('comments')
        .select(
          `
          id,
          photo_id,
          user_id,
          content,
          created_at,
          updated_at,
          user_settings!inner (
            username,
            avatar
          )
        `
        )
        .eq('photo_id', photoId)
        .order('created_at', { ascending: true })

      if (error) {
        console.error('Error fetching comments:', error)
        throw error
      }

      // Transform the data to match our Comment type
      const transformedComments: Comment[] = data.map((comment: any) => ({
        id: comment.id,
        photo_id: comment.photo_id,
        user_id: comment.user_id,
        content: comment.content,
        username: comment.user_settings.username,
        avatar: comment.user_settings.avatar,
        created_at: comment.created_at,
        updated_at: comment.updated_at
      }))

      comments.value[photoId] = transformedComments
      return transformedComments
    } catch (error) {
      console.error('Error in fetchComments:', error)
      return []
    } finally {
      loading.value[photoId] = false
    }
  }

  async function addComment(photoId: string, content: string): Promise<boolean> {
    if (supabase.user.status !== 'SignedIn') return false

    // Validate content length
    if (!content.trim() || content.length > 200) return false

    try {
      const { data, error } = await supabase.api
        .from('comments')
        .insert({
          photo_id: photoId,
          user_id: supabase.user.data.id,
          content: content.trim()
        })
        .select(
          `
          id,
          photo_id,
          user_id,
          content,
          created_at,
          updated_at,
          user_settings!inner (
            username,
            avatar
          )
        `
        )
        .single()

      if (error) {
        console.error('Error adding comment:', error)
        throw error
      }

      // Transform and add to local state
      const newComment: Comment = {
        id: data.id,
        photo_id: data.photo_id,
        user_id: data.user_id,
        content: data.content,
        username: (data as any).user_settings.username,
        avatar: (data as any).user_settings.avatar,
        created_at: data.created_at,
        updated_at: data.updated_at
      }

      // Add to existing comments array or create new one
      if (comments.value[photoId]) {
        comments.value[photoId].push(newComment)
      } else {
        comments.value[photoId] = [newComment]
      }

      return true
    } catch (error) {
      console.error('Error in addComment:', error)
      return false
    }
  }

  async function deleteComment(
    commentId: string,
    photoId: string,
    photoOwnerId?: string
  ): Promise<boolean> {
    if (supabase.user.status !== 'SignedIn') return false

    try {
      // RLS policies will automatically handle authorization
      const { error } = await supabase.api.from('comments').delete().eq('id', commentId)

      if (error) {
        console.error('Error deleting comment:', error)
        throw error
      }

      // Remove from local state
      if (comments.value[photoId]) {
        comments.value[photoId] = comments.value[photoId].filter(
          (comment) => comment.id !== commentId
        )
      }

      return true
    } catch (error) {
      console.error('Error in deleteComment:', error)
      return false
    }
  }

  function getComments(photoId: string): Comment[] {
    return comments.value[photoId] || []
  }

  function isLoading(photoId: string): boolean {
    return loading.value[photoId] || false
  }

  function clearComments(photoId: string) {
    delete comments.value[photoId]
    delete loading.value[photoId]
  }

  return {
    comments,
    loading,
    fetchComments,
    addComment,
    deleteComment,
    getComments,
    isLoading,
    clearComments
  }
})
